/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export default {
  namespaced: true,

  state: {
    calendarData: {},
    assignments: [],
    loading: false,
    error: null,
    currentDateRange: {
      from: null,
      to: null,
    },
    filters: {
      keyword: '',
      branchIds: [],
      staffId: null,
    },
  },

  getters: {
    assignments: state => state.assignments,
    isLoading: state => state.loading,
    error: state => state.error,
    calendarData: state => state.calendarData,
  },

  mutations: {
    SET_LOADING(state, value) { state.loading = value; },
    SET_ERROR(state, error) { state.error = error; },
    SET_CALENDAR_DATA(state, data) { state.calendarData = data; },
    SET_ASSIGNMENTS(state, assignments) { state.assignments = assignments; },
    SET_DATE_RANGE(state, { from, to }) {
      state.currentDateRange.from = from;
      state.currentDateRange.to = to;
    },
    SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
    CLEAR_FILTERS(state) {
      state.filters = { keyword: '', branchIds: [], staffId: null };
    },
    RESET_DATA(state) {
      state.calendarData = {};
      state.assignments = [];
      state.error = null;
    },
  },

  actions: {
    async fetchCalendarData({ commit, state, rootGetters }, { from, to }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const accountId = rootGetters.getCurrentAccountId;

      try {
        const params = { from, to };

        if (state.filters.staffId) params.staffId = state.filters.staffId;
        if (state.filters.branchIds.length > 0) params.branchId = state.filters.branchIds[0];

        const response = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/calendar`),
          { params }
        );

        commit('SET_CALENDAR_DATA', response.data);
        commit('SET_DATE_RANGE', { from, to });

        const assignments = transformCalendarToAssignments(response.data);
        commit('SET_ASSIGNMENTS', assignments);

        return assignments;
      } catch (err) {
        commit('SET_ERROR', err.response?.data || err.message);
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    setFilters({ commit }, filters) { commit('SET_FILTERS', filters); },
    clearFilters({ commit }) { commit('CLEAR_FILTERS'); },
    resetData({ commit }) { commit('RESET_DATA'); },
  },
};

function transformCalendarToAssignments(response) {
  const assignments = [];
  let assignmentId = 1;

  const results = response.results || [];

  results.forEach(result => {
    const { staff_id, staff_name, branch_id, branch_name, calendar } = result;

    if (calendar && Array.isArray(calendar)) {
      calendar.forEach(day => {
        const { 
          date, layer, time_slots, warning_reason, warning_type,
          source_id, source_type, assignment_date_range
        } = day;

        if (warning_reason && warning_type) {
          assignments.push({
            id: assignmentId++,
            staffId: staff_id,
            staffName: staff_name,
            branchId: branch_id,
            branchName: branch_name,
            startDate: date,
            endDate: date,
            resolutionLayer: layer,
            isWarning: true,
            warningReason: warning_reason,
            warningType: warning_type,
            timeSlots: [],
            sourceId: source_id,
            sourceType: source_type,
            assignmentDateRange: assignment_date_range,
            calendarDay: day,
          });
        } else if (time_slots && time_slots.length > 0) {
          time_slots.forEach(slot => {
            assignments.push({
              id: assignmentId++,
              staffId: staff_id,
              staffName: staff_name,
              branchId: branch_id,
              branchName: branch_name,
              startDate: date,
              endDate: date,
              resolutionLayer: layer,
              timeSlots: [{ startTime: slot.start_time, endTime: slot.end_time }],
              sourceId: source_id,
              sourceType: source_type,
              assignmentDateRange: assignment_date_range,
              calendarDay: day,
            });
          });
        } else if (source_id && source_type && layer !== 'branch_closed') {
          assignments.push({
            id: assignmentId++,
            staffId: staff_id,
            staffName: staff_name,
            branchId: branch_id,
            branchName: branch_name,
            startDate: date,
            endDate: date,
            resolutionLayer: layer,
            timeSlots: [],
            isFullDayUnavailable: layer === 'unavailable',
            sourceId: source_id,
            sourceType: source_type,
            assignmentDateRange: assignment_date_range,
            calendarDay: day,
          });
        }
      });
    }
  });

  return assignments;
}
